import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { YES_NO_OPTIONS } from "enums/dropdownValues"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import PaymentTerms from "./PaymentTerms"
import usePaymentDetails, { EPaymentDetailsFields } from "./usePaymentDetails"
import { EProjectFormPages } from "components/ProjectForm/EProjectFormPages"

const PaymentDetails = (props: ProjectFormPageProps) => {
    const { states, functions } = usePaymentDetails(props)
    const { formik, showPaymentTerms, soAmount, soAvailableAmount, remainingAmount, items, itemsTotal } = states

    const showError = formik.submitCount > 0
    const isLastPage = props.pages?.[props.pages.length - 1] === EProjectFormPages.PAYMENT_DETAILS
    const submitBtnText = isLastPage ? "Submit" : "Next"
    const totalAmountValue = Number(formik.values[EPaymentDetailsFields.TOTAL_AMOUNT]) || 0
    const maxPayableAmount = soAvailableAmount
    const isOverSoAmount = maxPayableAmount != null && totalAmountValue > maxPayableAmount
    const isNegativeRemaining = remainingAmount != null && remainingAmount < 0

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Specify payment terms (by agreement)?*
                            </Typography>
                            <RadioGroup
                                options={YES_NO_OPTIONS}
                                name={EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS}
                                value={formik.values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]}
                                onSelect={(value: any) => {
                                    formik.setFieldValue(EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS, value)
                                    functions.saveFormValuesToRedux(value, "specify_payment_terms")
                                }}
                            />
                            {showError && Boolean(formik.errors[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]) && (
                                <Typography color="error" fontSize="12px">
                                    Please specify payment terms
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    {showPaymentTerms && soAmount != null && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Sales Order amount (incl. GST)
                                </Typography>
                                <MuiTextField size="large" disabled value={`₹${soAmount.toLocaleString("en-IN")}`} />
                            </Box>
                        </Grid>
                    )}
                    {showPaymentTerms && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Total amount to be received with GST (in ₹)*
                                </Typography>
                                <MuiTextField
                                    placeholder="Enter Amount"
                                    size="large"
                                    type="number"
                                    value={formik.values[EPaymentDetailsFields.TOTAL_AMOUNT]}
                                    onChange={e => {
                                        formik.setFieldValue(EPaymentDetailsFields.TOTAL_AMOUNT, e.target.value)
                                        functions.saveFormValuesToRedux(e.target.value, "total_amount")
                                    }}
                                    error={
                                        isOverSoAmount ||
                                        (showError && Boolean(formik.errors[EPaymentDetailsFields.TOTAL_AMOUNT]))
                                    }
                                    inputProps={maxPayableAmount != null ? { max: maxPayableAmount } : undefined}
                                />
                                {maxPayableAmount != null && (
                                    <Typography fontSize="11px" color={isOverSoAmount ? "error" : "#70758D"}>
                                        {isOverSoAmount
                                            ? `Cannot exceed ₹${maxPayableAmount.toLocaleString("en-IN")}.`
                                            : `Up to ₹${maxPayableAmount.toLocaleString("en-IN")}.`}
                                    </Typography>
                                )}
                                {isNegativeRemaining && (
                                    <Typography fontSize="11px" color="error">
                                        Terms exceed the amount.
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {showPaymentTerms && items.length > 0 && (
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography fontSize="14px" variant="label" textTransform="none">
                                Deal amount calculation
                            </Typography>
                            {props.handleStepChange && (
                                <Typography
                                    variant="label"
                                    fontSize="11px"
                                    fontWeight={400}
                                    textTransform="none"
                                    onClick={() => props.handleStepChange?.(EProjectFormPages.MATERIAL_DETAILS)}
                                    sx={{
                                        textDecoration: "underline",
                                        textUnderlineOffset: "2px",
                                        color: "#0430C0",
                                        cursor: "pointer",
                                    }}>
                                    Edit in Material Details
                                </Typography>
                            )}
                        </Box>
                        <Box border="1px solid #E5E5E5" borderRadius="8px" overflow="hidden">
                            <Box display="flex" bgcolor="#FAFAFA" px={1.5} py={1} fontWeight={500} fontSize="12px">
                                <Box flex={1}>Item</Box>
                                <Box flex="0 0 70px">Qty</Box>
                                <Box flex="0 0 120px">Unit price</Box>
                                <Box flex="0 0 140px">Line total</Box>
                            </Box>
                            {items.map((item, i) => (
                                <Box
                                    key={`${item.product_id}-${i}`}
                                    display="flex"
                                    px={1.5}
                                    py={1}
                                    fontSize="12.5px"
                                    borderTop="1px solid #F0F0F0">
                                    <Box flex={1}>{item.display_name || item.sku_code}</Box>
                                    <Box flex="0 0 70px">{item.quantity}</Box>
                                    <Box flex="0 0 120px" color={item.price_source === "none" ? "error" : undefined}>
                                        {item.price_source === "none"
                                            ? "No price"
                                            : `₹${item.unit_price.toLocaleString("en-IN")}`}
                                    </Box>
                                    <Box flex="0 0 140px">₹{item.line_total.toLocaleString("en-IN")}</Box>
                                </Box>
                            ))}
                            {itemsTotal != null && (
                                <Box
                                    display="flex"
                                    px={1.5}
                                    py={1}
                                    fontSize="12.5px"
                                    fontWeight={500}
                                    borderTop="1px solid #F0F0F0">
                                    <Box flex={1}>Total</Box>
                                    <Box flex="0 0 70px" />
                                    <Box flex="0 0 120px" />
                                    <Box flex="0 0 140px">₹{itemsTotal.toLocaleString("en-IN")}</Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {showPaymentTerms && states.paymentTermsSource === "so" && (
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Typography fontSize="14px" variant="label" textTransform="none">
                            Payment terms (from the Sales Order)
                        </Typography>
                        <Box border="1px solid #E5E5E5" borderRadius="8px" overflow="hidden">
                            <Box display="flex" bgcolor="#FAFAFA" px={1.5} py={1} fontWeight={500} fontSize="12px">
                                <Box flex="0 0 110px">Term</Box>
                                <Box flex="0 0 70px">%</Box>
                                <Box flex="0 0 140px">Amount</Box>
                                <Box flex={1}>Condition</Box>
                            </Box>
                            {formik.values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ].map((term: any, i: number) => {
                                const pct = Number(term.pmt_percent) || 0
                                const amount = Math.round((totalAmountValue * pct) / 100)
                                return (
                                    <Box
                                        key={i}
                                        display="flex"
                                        px={1.5}
                                        py={1}
                                        fontSize="12.5px"
                                        borderTop="1px solid #F0F0F0">
                                        <Box flex="0 0 110px">Payment term {i + 1}</Box>
                                        <Box flex="0 0 70px">{pct}%</Box>
                                        <Box flex="0 0 140px">₹{amount.toLocaleString("en-IN")}</Box>
                                        <Box flex={1} color="#70758D">
                                            {term.condition_text || term.condition?.title || "-"}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                        <Typography fontSize="11px" color="#70758D">
                            Pulled from the linked Sales Order. Editable only once the project has payment terms of
                            its own.
                        </Typography>
                    </Box>
                )}

                {showPaymentTerms && states.paymentTermsSource !== "so" && (
                    <PaymentTerms
                        formik={formik}
                        financeTicketId={props.commonFormData?.common?.finance_ticket_id}
                        showEvidenceUpload={false}
                    />
                )}
            </Box>

            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                handleSubmit={() => formik.handleSubmit()}
                isLoading={states.isAddingPaymentTerms || states.isUpdatingPageData || formik.isSubmitting}
                submitButtonText={submitBtnText}
            />
        </>
    )
}

export default PaymentDetails
